export const LLM_CHART_COLORS = [
  'var(--scribane-black)',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#3b82f6',
  '#f97316',
  '#06b6d4',
];

export const COST_PER_CREDIT_SIMILARITY_TOLERANCE = 0.1;
export const CREDITS_PER_COST_DISPLAY_UNIT = 1000;

export type CostEfficiencyLevel = 'below' | 'similar' | 'above' | 'unknown';

export function buildTypeColorMap(types: string[]): Record<string, string> {
  const colorMap: Record<string, string> = {};
  types.forEach((type, index) => {
    colorMap[type] = LLM_CHART_COLORS[index % LLM_CHART_COLORS.length];
  });
  return colorMap;
}

export function collectOrderedChartTypes(
  callsByType: Array<{ name: string }>,
  costByType: Array<{ name: string }>,
): string[] {
  const orderedTypes = callsByType.map((entry) => entry.name);
  const extraTypes = costByType
    .map((entry) => entry.name)
    .filter((name) => !orderedTypes.includes(name))
    .sort((left, right) => left.localeCompare(right));

  return [...orderedTypes, ...extraTypes];
}

export function computeCostPerCredit(totalCost: number, totalCreditsUsed: number): number | null {
  if (totalCreditsUsed <= 0 || totalCost <= 0) {
    return null;
  }

  return totalCost / totalCreditsUsed;
}

export function computeCostPer1kCredits(costPerCredit: number | null): number | null {
  if (costPerCredit === null) {
    return null;
  }

  return costPerCredit * CREDITS_PER_COST_DISPLAY_UNIT;
}

export function computeCallCostPerCredit(costUsd: number | null, creditsUsed: number | null): number | null {
  if (costUsd === null || costUsd <= 0 || creditsUsed === null || creditsUsed <= 0) {
    return null;
  }

  return costUsd / creditsUsed;
}

export function getCostEfficiencyLevel(
  callCostPerCredit: number | null,
  averageCostPerCredit: number | null,
  tolerance: number = COST_PER_CREDIT_SIMILARITY_TOLERANCE,
): CostEfficiencyLevel {
  if (callCostPerCredit === null || averageCostPerCredit === null || averageCostPerCredit <= 0) {
    return 'unknown';
  }

  const lowerBound = averageCostPerCredit * (1 - tolerance);
  const upperBound = averageCostPerCredit * (1 + tolerance);

  if (callCostPerCredit < lowerBound) {
    return 'below';
  }

  if (callCostPerCredit > upperBound) {
    return 'above';
  }

  return 'similar';
}

export function getCostEfficiencyColor(level: CostEfficiencyLevel): string {
  switch (level) {
    case 'below':
      return '#10b981';
    case 'similar':
      return '#6b7280';
    case 'above':
      return '#ef4444';
    default:
      return '#6b7280';
  }
}
